import React, { useState } from "react";
import { Eye, EyeOff, Library, ShieldCheck } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { useAuth } from "../context/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function AccountSignup() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    if (password !== confirm) {
      toast.error("Passwords do not match");
      return;
    }
    setBusy(true);
    try {
      await axios.post(API + "/auth/register", {
        display_name: displayName.trim(),
        email: email.trim(),
        password,
      });
      await refresh();
      toast.success("Your account is ready.");
      navigate("/my-library", { replace: true });
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not create account");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-parchment">
      <div className="mx-auto max-w-lg px-4 py-10 sm:px-6 sm:py-14">
        <div className="text-center">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-xl bg-royal-blue">
            <Library className="h-7 w-7 text-kingdom-gold" />
          </div>
          <h1 className="mt-4 font-heading text-3xl text-royal-blue">Create your Yashar’al account</h1>
          <p className="mt-2 text-slate-600">
            One free account starts with My Library and can later carry your Nation, subscriber, and Yashar’al Live access.
          </p>
        </div>

        <form onSubmit={submit} className="mt-7 space-y-5 rounded-xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
          <div className="flex gap-3 border border-kingdom-gold/30 bg-kingdom-gold/10 p-4 text-sm text-slate-700">
            <ShieldCheck className="h-5 w-5 shrink-0 text-royal-blue" />
            <p>
              Creating this account does not make you YaSHaRUN, start a paid subscription, or activate Yashar’al Live. Those can be added later to the same login.
            </p>
          </div>

          <div>
            <label className="text-sm font-semibold text-slate-700">Display name</label>
            <Input className="mt-2" value={displayName} onChange={(event) => setDisplayName(event.target.value)} required />
          </div>

          <div>
            <label className="text-sm font-semibold text-slate-700">Email</label>
            <Input className="mt-2" type="email" autoComplete="email" value={email} onChange={(event) => setEmail(event.target.value)} required />
          </div>

          <div>
            <label className="text-sm font-semibold text-slate-700">Password</label>
            <div className="relative mt-2">
              <Input
                type={showPassword ? "text" : "password"}
                autoComplete="new-password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                className="pr-11"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword((value) => !value)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            <p className="mt-1 text-xs text-slate-500">At least 8 characters, one uppercase letter, and one special character.</p>
          </div>

          <div>
            <label className="text-sm font-semibold text-slate-700">Confirm password</label>
            <Input
              className="mt-2"
              type={showPassword ? "text" : "password"}
              autoComplete="new-password"
              value={confirm}
              onChange={(event) => setConfirm(event.target.value)}
              required
            />
          </div>

          <Button className="btn-gold h-11 w-full" disabled={busy || !displayName.trim() || !email.trim() || !password || !confirm}>
            {busy ? "Creating…" : "Create free account"}
          </Button>

          <div className="text-center text-sm text-slate-600">
            Already have an account? <Link to="/login?next=/my-library" className="font-semibold text-royal-blue underline">Sign in</Link>
          </div>
        </form>
      </div>
    </div>
  );
}
